import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Modal,
  PanResponder,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
  type PanResponderGestureState,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RADIUS, SPACE } from '../theme/spacing';
import { TYPE } from '../theme/typography';
import { useEventCallback } from '../util/useEventCallback';
import { useKeyboardOverlap } from '../util/useKeyboardOverlap';
import { useReduceMotion } from './Skeleton';
import { ToastOutlet } from './ToastOutlet';

/**
 * Нижний лист с ручкой и закрытием свайпом вниз.
 *
 * <p><b>Зачем.</b> В приложении 38 системных `Modal`, из них 12 — на одном экране «Чаты». Все
 * полноэкранные: карточка по центру, затемнение вокруг, закрыть можно только кнопкой. Нативная
 * модель другая — лист выезжает снизу, тянется пальцем и уходит вниз тем же жестом, которым
 * его позвали. Свайп вниз — первое, что человек пробует, и до сих пор он не делал ничего.
 *
 * <p><b>Почему не библиотека и не Reanimated.</b> `@gorhom/bottom-sheet` тянет за собой
 * Reanimated, а он в этом проекте на вебе подменён пустой заглушкой
 * (`webpack.config.cjs` → `src/shims/emptyModule.js`): лист, собранный на нём, работал бы на
 * телефоне и падал в браузере. По той же причине здесь не `Gesture.Pan` из gesture-handler —
 * его колбэки при установленном Reanimated становятся worklet'ами, и `Animated.Value.setValue`
 * из них не вызвать. `PanResponder` и `Animated` — часть самого React Native: одинаково живые
 * на всех трёх платформах и без единой зависимости.
 *
 * <p><b>Детенты.</b> Два, а не произвольная высота: `medium` под список или форму в несколько
 * полей, `large` под содержимое, которое само себе экран. Промежуточные ступени требуют от
 * человека решения «на какую высоту тянуть», которого он не просил принимать.
 */

/** Доля высоты экрана, которую занимает лист. */
const DETENT_FRACTION = { medium: 0.6, large: 0.92 } as const;

/** `auto` выше этой доли не растёт: дальше лист перестаёт быть листом. */
const AUTO_MAX_FRACTION = DETENT_FRACTION.large;

type SheetDetent = keyof typeof DETENT_FRACTION | 'auto';

/** Порог закрытия: протянули больше четверти листа — отпускать уже некуда, лист уходит. */
const DISMISS_DISTANCE_FRACTION = 0.25;
/** Либо бросок вниз: быстрый жест закрывает, даже если пройдено немного. */
const DISMISS_VELOCITY = 0.5;

const OPEN_DURATION_MS = 240;
const CLOSE_DURATION_MS = 180;

export type BottomSheetPalette = {
  /** Фон самого листа. */
  card: string;
  /** Затемнение под листом. */
  backdrop: string;
  /** Полоска-ручка вверху. */
  handle: string;
  /** Цвет заголовка. */
  title: string;
};

type BottomSheetProps = {
  visible: boolean;
  onClose: () => void;
  palette: BottomSheetPalette;
  /** Заголовок листа. Без него шапка не рисуется — остаётся только ручка. */
  title?: string;
  /** `auto` (по умолчанию) — высота по содержимому; `medium`/`large` — для списков внутри. */
  detent?: SheetDetent;
  /** Доп. стиль содержимого (например, `gap`). */
  contentStyle?: StyleProp<ViewStyle>;
  children: React.ReactNode;
  accessibilityLabel?: string;
  /**
   * Слой поверх листа — подтверждение разрушающего действия, запущенного из самого листа.
   *
   * <p>Отдельным `Modal` его показать нельзя: на Android второй системный Modal поверх уже
   * открытого уходит ПОД него, и кнопка выглядит сломанной (это уже ловили в модалке BOSS).
   * Поэтому подтверждение живёт внутри того же слоя, но рисуется после листа — и потому над ним.
   */
  overlay?: React.ReactNode;
};

export function BottomSheet({
  visible,
  onClose,
  palette,
  title,
  detent = 'auto',
  contentStyle,
  children,
  accessibilityLabel,
  overlay,
}: BottomSheetProps) {
  const { height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const reduceMotion = useReduceMotion();
  /** Лист стоит на клавиатуре, а не под ней: см. `useKeyboardOverlap`. */
  const keyboard = useKeyboardOverlap(visible);

  /**
   * Потолок: у `auto` — та же доля, что у `large`; дальше содержимое прокручивается внутри. С
   * клавиатурой — не выше места над ней: иначе поднятый лист уходил бы шапкой за статус-бар.
   */
  const maxHeight = Math.round(
    Math.min(
      windowHeight * (detent === 'auto' ? AUTO_MAX_FRACTION : DETENT_FRACTION[detent]),
      keyboard > 0 ? windowHeight - keyboard - insets.top - SPACE.lg : Infinity,
    ),
  );
  /** Сколько лист занял на самом деле — только у `auto`, приходит из `onLayout`. */
  const [autoHeight, setAutoHeight] = useState<number | null>(null);
  const sheetHeight = detent === 'auto' ? Math.min(autoHeight ?? maxHeight, maxHeight) : maxHeight;

  /**
   * Смещение листа вниз от его открытого положения. Это `Animated.Value`, заведённый один раз, а
   * не число в состоянии: во время перетаскивания оно меняется каждый кадр, и перерисовывать на
   * этом дерево незачем.
   */
  const [translateY] = useState(() => new Animated.Value(sheetHeight));
  const [backdrop] = useState(() => new Animated.Value(0));
  /** Выезд уже сыгран для текущего открытия: обмер содержимого не должен повторять его. */
  const openedRef = useRef(false);

  const animateTo = useCallback(
    (toSheet: number, toBackdrop: number, duration: number, done?: () => void) => {
      if (reduceMotion) {
        translateY.setValue(toSheet);
        backdrop.setValue(toBackdrop);
        done?.();
        return;
      }
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: toSheet,
          duration,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(backdrop, {
          toValue: toBackdrop,
          duration,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start(({ finished }) => {
        if (finished) done?.();
      });
    },
    [backdrop, reduceMotion, translateY],
  );

  useEffect(() => {
    if (!visible) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    // У `auto` до обмера высота неизвестна, а выезжать не с неё — значит либо дёрнуть лист
    // из-за нижнего края, либо мигнуть им на месте. Кадр ожидания не виден: до обмера лист
    // стоит ниже экрана.
    if (detent === 'auto' && autoHeight == null) return;
    openedRef.current = true;
    translateY.setValue(sheetHeight);
    backdrop.setValue(0);
    animateTo(0, 1, OPEN_DURATION_MS);
    // Закрытие анимируется в `close()`: реагировать здесь на снятие `visible` поздно —
    // системный Modal к этому моменту уже снят с экрана вместе с анимацией.
  }, [animateTo, autoHeight, backdrop, detent, sheetHeight, translateY, visible]);

  const close = useCallback(() => {
    animateTo(sheetHeight, 0, CLOSE_DURATION_MS, onClose);
  }, [animateTo, onClose, sheetHeight]);

  /**
   * Обработчики жеста для `PanResponder`, который создаётся один раз: пересоздать его посреди
   * жеста значит потерять накопленное смещение. Поэтому сами обработчики постоянные, но видят
   * свежие значения — у `auto` высота меняется после обмера, и порог закрытия считался бы по
   * той, что была на первом кадре.
   */
  const onGrabMove = useEventCallback((g: PanResponderGestureState) => {
    // Вверх лист не тянется: детент один, тянуть выше открытого положения некуда.
    translateY.setValue(Math.max(0, g.dy));
  });
  const onGrabRelease = useEventCallback((g: PanResponderGestureState) => {
    const farEnough = g.dy > sheetHeight * DISMISS_DISTANCE_FRACTION;
    const fastEnough = g.vy > DISMISS_VELOCITY;
    if (farEnough || fastEnough) {
      animateTo(sheetHeight, 0, CLOSE_DURATION_MS, onClose);
    } else {
      animateTo(0, 1, OPEN_DURATION_MS);
    }
  });
  const onGrabTerminate = useEventCallback(() => animateTo(0, 1, OPEN_DURATION_MS));

  /**
   * Тянуть можно за ручку и шапку, но не за содержимое: внутри листа бывают списки, и жест
   * вниз там означает прокрутку, а не закрытие. Разделение по зоне — самый предсказуемый
   * способ развести эти два смысла без гонки распознавателей.
   */
  const [pan] = useState(() =>
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => g.dy > 2 && Math.abs(g.dy) > Math.abs(g.dx),
      onPanResponderMove: (_e, g) => onGrabMove(g),
      onPanResponderRelease: (_e, g) => onGrabRelease(g),
      onPanResponderTerminate: () => onGrabTerminate(),
    }),
  );

  return (
    <Modal
      visible={visible}
      transparent
      // Появление рисуем сами: системная анимация наложилась бы на выезд листа.
      animationType="none"
      onRequestClose={close}
      accessibilityLabel={accessibilityLabel ?? title}>
      <View style={sheetStyles.root}>
        <Animated.View
          style={[
            StyleSheet.absoluteFill,
            { backgroundColor: palette.backdrop, opacity: backdrop },
          ]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={close}
            accessibilityRole="button"
            accessibilityLabel="close"
          />
        </Animated.View>

        <Animated.View
          onLayout={
            detent === 'auto'
              ? (e) => {
                  const h = Math.round(e.nativeEvent.layout.height);
                  setAutoHeight((prev) => (prev === h ? prev : h));
                }
              : undefined
          }
          style={[
            sheetStyles.sheet,
            detent === 'auto' ? { maxHeight } : { height: maxHeight },
            {
              backgroundColor: palette.card,
              paddingBottom: insets.bottom + SPACE.lg,
              // Отступ снизу, а не сдвиг: сдвиг уже занят жестом закрытия, и одно сложение с
              // другим уводило бы порог закрытия.
              marginBottom: keyboard,
              transform: [{ translateY }],
            },
          ]}>
          <View {...pan.panHandlers} style={sheetStyles.grabArea}>
            <View style={[sheetStyles.handle, { backgroundColor: palette.handle }]} />
            {title ? (
              <Text style={[sheetStyles.title, { color: palette.title }]} numberOfLines={1}>
                {title}
              </Text>
            ) : null}
          </View>
          <View
            style={[
              detent === 'auto' ? sheetStyles.contentAuto : sheetStyles.content,
              contentStyle,
            ]}>
            {children}
          </View>
        </Animated.View>

        {overlay}
        {/* Свой вывод уведомлений: корневой рисуется под окном Modal (см. toastBridge). */}
        {visible ? <ToastOutlet bottomOffset={keyboard} /> : null}
      </View>
    </Modal>
  );
}

const sheetStyles = StyleSheet.create({
  root: { flex: 1, justifyContent: 'flex-end' },
  sheet: {
    // Широкое окно (десктоп, веб, планшет): лист на всю ширину монитора — поле статуса в
    // 1400 px и кнопки по 700. Предел по центру; телефон уже предела, у него всё как было.
    width: '100%',
    maxWidth: 640,
    alignSelf: 'center',
    borderTopLeftRadius: RADIUS.lg,
    borderTopRightRadius: RADIUS.lg,
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 -8px 32px rgba(15, 23, 42, 0.28)' },
      ios: {
        shadowColor: '#0f172a',
        shadowOffset: { width: 0, height: -8 },
        shadowOpacity: 0.28,
        shadowRadius: 16,
      },
      android: { elevation: 24 },
      default: {},
    }),
  },
  /** Зона перетаскивания: ручка плюс заголовок. Ниже начинается содержимое. */
  grabArea: {
    paddingTop: SPACE.md,
    paddingHorizontal: SPACE.lg,
    paddingBottom: SPACE.md,
    gap: SPACE.md,
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: RADIUS.pill,
  },
  title: { ...TYPE.title },
  content: { flex: 1, minHeight: 0, paddingHorizontal: SPACE.lg, gap: SPACE.md },
  /**
   * У `auto` содержимое задаёт высоту, а не получает её: `flex: 1` растянул бы лист обратно
   * до потолка — ровно до того, от чего `auto` и заводился.
   */
  contentAuto: { flexShrink: 1, paddingHorizontal: SPACE.lg, gap: SPACE.md },
});
